import { Box } from '@oresundsbron/bridge-ui';
import { Card, Typography, Steps } from '@oresundsbron/bridge-ui';
import {
  Selectors,
  Steps as FormSteps,
  useFormStore,
} from '@oresundsbron/use-form';
import { findFirst, findIndex, mapWithIndex } from 'fp-ts/lib/Array';
import { pipe } from 'fp-ts/lib/function';
import { getOrElse, getOrElseW } from 'fp-ts/lib/Option';
import { Trans, useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';
import { ContractTranslationKey } from '../lib/contract-translations';
import { ContractName } from './ContractName';

export const SignUpProgress: FC<{
  total: { label: string; value: string };
  contracts: { label: ContractTranslationKey; value: string; main: boolean }[];
  ns: string;
  business: boolean;
}> = ({ total, contracts, ns, business }) => {
  const { t } = useTranslation([ns, 'common'], { nsMode: 'fallback' });
  const activeFormStep = useFormStore(FormSteps.getActiveStep);
  const availableSteps = useFormStore(Selectors.getAvailableSteps);
  const skippedSteps = useFormStore(Selectors.getSkippedSteps);

  const activeIndex = useMemo(
    () =>
      pipe(
        availableSteps,
        findIndex((x) => x.namespace === activeFormStep),
        getOrElse(() => -1)
      ),
    [activeFormStep, availableSteps]
  );

  const steps = useMemo(
    () =>
      pipe(
        availableSteps,
        mapWithIndex((i, x) => {
          return {
            ...x,
            active:
              (i <= activeIndex &&
                !skippedSteps.find((s) => s.step === x.namespace) &&
                x.completed) ||
              (i >= activeIndex &&
                !skippedSteps.find((s) => s.step === x.namespace) &&
                x.completed) ||
              activeIndex === i,
            completed: x.completed,
          };
        })
      ),
    [activeIndex, availableSteps, skippedSteps]
  );

  const mainContract = useMemo(
    () =>
      pipe(
        contracts,
        findFirst((x) => x.main),
        getOrElseW(() => undefined)
      ),
    [contracts]
  );

  return (
    <Card.Root as="aside" className="sticky top-[10vh] ml-auto max-w-[350px]">
      <Card.Header className="bg-primary-800">
        <Typography
          intent="title"
          size="lg"
          color="white"
          as="h3"
          className="pb-1 pt-3 text-center"
        >
          {mainContract ? (
            <Trans ns={ns} i18nKey="form.sections.progress.title">
              <ContractName contract={mainContract.label} />
            </Trans>
          ) : null}
        </Typography>
      </Card.Header>
      <Card.Content>
        <Typography as="h4" className="sr-only" id="form-progress-title">
          {t('form.sections.progress.stepsTitle')}
        </Typography>
        <Steps.Root aria-labelledby="form-progress-title">
          {steps.map(
            ({ namespace, titleTranslationKey, active, completed }, i) => (
              <Steps.Item
                nr={i + 1}
                key={namespace}
                active={active}
                completed={completed}
              >
                {active ? (
                  <Box
                    as="a"
                    href={`#${namespace}`}
                    aria-current={
                      activeFormStep === namespace ? 'page' : undefined
                    }
                  >
                    {t(titleTranslationKey)}
                  </Box>
                ) : (
                  t(titleTranslationKey)
                )}
              </Steps.Item>
            )
          )}
        </Steps.Root>
      </Card.Content>
      <Card.Footer className="border-t">
        <Typography intent="title" size="sm" as="h4">
          {t('form.sections.progress.orderTitle')}
        </Typography>
        <Box as="ol">
          {contracts.map(({ label, value }, i) => (
            <Box as="li" key={i} className="mt-4">
              <Box className="flex justify-between">
                <Typography className="text-primary-800">
                  <ContractName contract={label} />
                </Typography>
                <Typography className="text-primary-800">{value}</Typography>
              </Box>
              {business ? (
                <Typography className="text mt-[-4px] text-right text-xs font-light text-primary-800">
                  {t('form.labels.exclVAT')}
                </Typography>
              ) : null}
            </Box>
          ))}
          <Box as="li" className="mt-4 flex flex-col border-t pt-4">
            <Box className="flex justify-between">
              <Typography>{total.label}</Typography>
              <Typography>{total.value}</Typography>
            </Box>
            {business ? (
              <Typography className="text mt-[-4px] text-right text-xs font-light text-primary-800">
                {t('form.labels.exclVAT')}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Card.Footer>
    </Card.Root>
  );
};
